import React, { FC } from "react";
import styled, { css } from "styled-components";
import { useTranslation } from "react-i18next";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";

// Assets
import Instagram from "./../images/svg/instagram.svg";
import Facebook from "./../images/svg/facebook.svg";
import Map from "./../images/svg/map-pointer.svg";
import Global from "./../images/svg/global.svg";

// Styles
const SocialButton = css`
  height: 30px;
  transition: all 0.125s !important;
  width: 30px;

  @media (min-width: 768px) {
    height: 32px;
    width: 32px;
  }

  &:hover {
    fill: var(--terracotta);
  }
`;

const InstagramIcon = styled((props) => <Instagram {...props} />)`
  ${SocialButton}
`;

const FacebookIcon = styled((props) => <Facebook {...props} />)`
  ${SocialButton}
`;

const MapIcon = styled((props) => <Map {...props} />)`
  ${SocialButton}
`;

const GlobalIcon = styled((props) => <Global {...props} />)`
  ${SocialButton}
`;

interface IProps {
  img?: any;
  alt?: string;
  heading: string;
  time?: string;
  distance?: string;
  description?: string;
  facebook?: string;
  instagram?: string;
  map?: string;
  website?: string;
  youtube?: string;
}

const CardHorizontal: FC<IProps> = ({
  img,
  alt,
  heading,
  time,
  distance,
  description,
  facebook,
  instagram,
  map,
  website,
  youtube,
}) => {
  const { t } = useTranslation();
  const hasVideo = Boolean(youtube);

  return (
    <div className="card mb-3">
      <Row className="align-items-center g-0">
        <Col md={hasVideo ? 5 : 3} xl={hasVideo ? 4 : 2}>
          <div className="d-flex justify-content-center p-3">
            {hasVideo ? (
              <div className="ratio ratio-16x9 w-100">
                <iframe
                  src={`https://www.youtube-nocookie.com/embed/${youtube}`}
                  title={alt}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="rounded"
                />
              </div>
            ) : (
              <img src={img} className="img-fluid rounded" alt={alt} />
            )}
          </div>
        </Col>
        <Col md={hasVideo ? 7 : 9} xl={hasVideo ? 8 : 10} className="d-flex align-items-center">
          <div className="card-body">
            <div className="d-flex flex-column flex-md-row">
              <h4 className="card-title d-inline-block me-0 me-md-2 lh-base">
                {heading}
              </h4>
              <span className="mb-2 mb-md-0">
                {typeof time !== "undefined" || typeof distance !== "undefined"
                  ? "("
                  : ""}
                {typeof time !== "undefined" ? `${time} / ` : ""}
                {typeof distance !== "undefined" && distance}
                {typeof time !== "undefined" || typeof distance !== "undefined"
                  ? ")"
                  : ""}
              </span>
            </div>
            {description !== "undefined" && (
              <p className="card-text">{description}</p>
            )}
            <div>
              {website && (
                <a
                  href={website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="muted-link social"
                >
                  {t(`visitWebsite`)}
                  <GlobalIcon />
                </a>
              )}
              {instagram && (
                <a
                  href={instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="muted-link social"
                >
                  {t(`followInstagram`)}
                  <InstagramIcon />
                </a>
              )}
              {facebook && (
                <a
                  href={facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="muted-link social"
                >
                  {t(`followFacebook`)}
                  <FacebookIcon />
                </a>
              )}
              {map && (
                <a
                  href={map}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="muted-link social"
                >
                  {t(`visitMap`)}
                  <MapIcon />
                </a>
              )}
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default CardHorizontal;
